/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import { toast } from "react-toastify";
import {
  FaAlignCenter,
  FaAlignLeft,
  FaAlignRight,
  FaBold,
  FaCode,
  FaColumns,
  FaCloudUploadAlt,
  FaEraser,
  FaFileCode,
  FaFileImage,
  FaFont,
  FaHighlighter,
  FaImage,
  FaIndent,
  FaItalic,
  FaLink,
  FaListOl,
  FaListUl,
  FaMinus,
  FaOutdent,
  FaPlusSquare,
  FaQuoteRight,
  FaRedo,
  FaStrikethrough,
  FaTable,
  FaTasks,
  FaTrashAlt,
  FaUnderline,
  FaUndo,
  FaVideo,
} from "react-icons/fa";
import { Document } from "@tiptap/extension-document";
import { Text } from "@tiptap/extension-text";
import { Paragraph } from "@tiptap/extension-paragraph";
import {
  Dropcursor,
  Gapcursor,
  Placeholder,
  TrailingNode,
} from "@tiptap/extensions";
import { HardBreak } from "@tiptap/extension-hard-break";
import { TextStyle } from "@tiptap/extension-text-style";
import { ListItem } from "@tiptap/extension-list";
import { History } from "reactjs-tiptap-editor/lib/History.js";
import { Clear } from "reactjs-tiptap-editor/lib/Clear.js";
import { FontFamily } from "reactjs-tiptap-editor/lib/FontFamily.js";
import { FontSize } from "reactjs-tiptap-editor/lib/FontSize.js";
import { Heading } from "reactjs-tiptap-editor/lib/Heading.js";
import { Bold } from "reactjs-tiptap-editor/lib/Bold.js";
import { Italic } from "reactjs-tiptap-editor/lib/Italic.js";
import { TextUnderline } from "reactjs-tiptap-editor/lib/TextUnderline.js";
import { Strike } from "reactjs-tiptap-editor/lib/Strike.js";
import { MoreMark } from "reactjs-tiptap-editor/lib/MoreMark.js";
import { Color } from "reactjs-tiptap-editor/lib/Color.js";
import { Highlight } from "reactjs-tiptap-editor/lib/Highlight.js";
import { BulletList } from "reactjs-tiptap-editor/lib/BulletList.js";
import { OrderedList } from "reactjs-tiptap-editor/lib/OrderedList.js";
import { TextAlign } from "reactjs-tiptap-editor/lib/TextAlign.js";
import { Indent } from "reactjs-tiptap-editor/lib/Indent.js";
import { Link } from "reactjs-tiptap-editor/lib/Link.js";
import { Image } from "reactjs-tiptap-editor/lib/Image.js";
import { Video } from "reactjs-tiptap-editor/lib/Video.js";
import { Blockquote } from "reactjs-tiptap-editor/lib/Blockquote.js";
import { HorizontalRule } from "reactjs-tiptap-editor/lib/HorizontalRule.js";
import { Code } from "reactjs-tiptap-editor/lib/Code.js";
import { CodeView } from "reactjs-tiptap-editor/lib/CodeView.js";
import {
  Column,
  ColumnNode,
  MultipleColumnNode,
} from "reactjs-tiptap-editor/lib/Column.js";
import { Table } from "reactjs-tiptap-editor/lib/Table.js";
import { TaskList } from "reactjs-tiptap-editor/lib/TaskList.js";
import { Extension } from "@tiptap/core";
import { SearchAndReplace } from "reactjs-tiptap-editor/lib/SearchAndReplace.js";
import { TextDirection } from "reactjs-tiptap-editor/lib/TextDirection.js";
import { Callout } from "reactjs-tiptap-editor/lib/Callout.js";
import "reactjs-tiptap-editor/lib/style.css";
import "./BlogRichTextEditor.css";

const colorList = [
  "#000000",
  "#262626",
  "#595959",
  "#8C8C8C",
  "#BFBFBF",
  "#FFFFFF",
  "#F5222D",
  "#FA8C16",
  "#FADB14",
  "#52C41A",
  "#13C2C2",
  "#1890FF",
  "#2F54EB",
  "#722ED1",
  "#EB2F96",
  "#FFE8E6",
  "#FFD591",
  "#FCFCCA",
  "#D3F5F0",
  "#D4EEFC",
  "#EFE1FA",
  "#FFADD2",
];

const fontFamilyList = [
  "WorkSans-Regular",
  "Cagily",
  "Arial",
  "Georgia",
  "Helvetica",
  "Times New Roman",
  "Verdana",
  "Courier New",
];

const fontSizeList = [
  "Default",
  "12px",
  "14px",
  "16px",
  "18px",
  "20px",
  "24px",
  "28px",
  "36px",
  "48px",
];

const lineHeightList = [
  "Default",
  "0.5",
  "0.75",
  "1",
  "1.15",
  "1.5",
  "2",
  "2.5",
  "3",
];

const imageSizeList = [
  { label: "Auto", value: "auto" },
  { label: "Wide", value: "720" },
  { label: "Large", value: "540" },
  { label: "Medium", value: "360" },
  { label: "Small", value: "240" },
];

const parsePixelValue = (value) => {
  const normalizedValue = String(value || "").trim();
  if (!/^\d+(?:\.\d+)?px$/i.test(normalizedValue)) return null;

  const parsedValue = Number.parseFloat(normalizedValue);
  return parsedValue > 0 ? parsedValue : null;
};

const parseImagePixelDimension = (element, dimension) => {
  const styleValue = String(element.style?.[dimension] || "").trim();

  if (styleValue) {
    const parsedStyleValue = parsePixelValue(styleValue);
    return parsedStyleValue || null;
  }

  const attributeValue = String(
    element.getAttribute(dimension) || "",
  ).trim();
  if (!/^\d+(?:\.\d+)?$/.test(attributeValue)) return null;

  const parsedAttributeValue = Number.parseFloat(attributeValue);
  return parsedAttributeValue > 0 ? parsedAttributeValue : null;
};

const BlockLineHeight = Extension.create({
  name: "lineHeight",

  addGlobalAttributes() {
    return [
      {
        types: ["paragraph", "heading"],
        attributes: {
          lineHeight: {
            default: null,
            parseHTML: (element) => element.style.lineHeight || null,
            renderHTML: (attributes) => {
              if (!attributes.lineHeight) return {};
              return { style: `line-height: ${attributes.lineHeight}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setLineHeight:
        (lineHeight) =>
        ({ commands }) => {
          return ["paragraph", "heading"].every((type) =>
            commands.updateAttributes(type, { lineHeight }),
          );
        },
      unsetLineHeight:
        () =>
        ({ commands }) => {
          return ["paragraph", "heading"].every((type) =>
            commands.updateAttributes(type, { lineHeight: null }),
          );
        },
    };
  },
});

const defaultFontFamily = "WorkSans-Regular";

const ToolbarButton = ({ active, children, disabled, onClick, title }) => (
  <button
    type="button"
    // title={title}
    aria-label={title}
    data-tooltip={title}
    disabled={disabled}
    className={`blog-tiptap-button${active ? " active" : ""}`}
    onMouseDown={(event) => {
      event.preventDefault();
      if (!disabled) onClick();
    }}
  >
    {children}
  </button>
);

const ToolbarSelect = ({ children, disabled, onChange, title, value }) => (
  <span className="blog-tiptap-select-wrapper" data-tooltip={title}>
    <select
      aria-label={title}
      className="blog-tiptap-select"
      disabled={disabled}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onMouseDown={(event) => event.stopPropagation()}
    >
      {children}
    </select>
  </span>
);

const getBlockValue = (editor) => {
  if (!editor) return "paragraph";
  if (editor.isActive("heading", { level: 1 })) return "1";
  if (editor.isActive("heading", { level: 2 })) return "2";
  if (editor.isActive("heading", { level: 3 })) return "3";
  return "paragraph";
};

const runIfAvailable = (editor, command, payload) => {
  if (!editor?.commands?.[command]) return;
  editor.commands.focus();
  editor.commands[command](payload);
};

const runListItemCommand = (editor, command, itemName) => {
  const chain = editor?.chain?.().focus();
  if (!chain || typeof chain[command] !== "function") return false;

  return chain[command](itemName).run();
};

const isListSelection = (editor) =>
  editor?.isActive("bulletList") || editor?.isActive("orderedList");

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const getColumnImages = (editor) => {
  if (!editor?.view?.dom) return [];

  const imageNodes = [];
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name !== "image") return;

    const $pos = editor.state.doc.resolve(pos);
    const isInsideColumn = Array.from(
      { length: $pos.depth + 1 },
      (_, index) => $pos.node(index),
    ).some((ancestor) => ancestor.type.name === "column");

    if (isInsideColumn) {
      imageNodes.push({ node, pos });
    }
  });

  const imageElements = Array.from(
    editor.view.dom.querySelectorAll(
      ".column .image-view__body__image",
    ),
  );

  return imageNodes
    .map((imageNode, index) => ({
      ...imageNode,
      element: imageElements[index],
    }))
    .filter(({ element }) => element);
};

const getColumnContentWidth = (imageElement) => {
  const column = imageElement.closest(".column");
  if (!column) return 0;

  const columnStyle = window.getComputedStyle(column);
  const horizontalPadding =
    Number.parseFloat(columnStyle.paddingLeft || 0) +
    Number.parseFloat(columnStyle.paddingRight || 0);

  return Math.max(0, column.clientWidth - horizontalPadding);
};

const syncColumnImagePercentages = (editor) => {
  if (!editor || editor.isDestroyed) return;

  const transaction = editor.state.tr;
  let hasChanges = false;

  getColumnImages(editor).forEach(({ element, node, pos }) => {
    const columnWidth = getColumnContentWidth(element);
    if (!columnWidth) return;

    const imageWidth = element.getBoundingClientRect().width;
    const widthPercent = Math.min(
      100,
      Math.max(1, Number(((imageWidth / columnWidth) * 100).toFixed(2))),
    );
    const currentWidthPercent = Number(node.attrs.columnWidthPercent);

    if (
      !Number.isFinite(currentWidthPercent) ||
      Math.abs(currentWidthPercent - widthPercent) > 0.1
    ) {
      transaction.setNodeMarkup(pos, undefined, {
        ...node.attrs,
        columnWidthPercent: widthPercent,
      });
      hasChanges = true;
    }
  });

  if (hasChanges) {
    editor.view.dispatch(transaction);
  }
};

const indentSelection = (editor) => {
  if (
    editor?.isActive("taskList") &&
    runListItemCommand(editor, "sinkListItem", "taskItem")
  ) {
    return;
  }

  if (
    isListSelection(editor) &&
    runListItemCommand(editor, "sinkListItem", "listItem")
  ) {
    return;
  }

  runIfAvailable(editor, "indent");
};

const outdentSelection = (editor) => {
  if (
    editor?.isActive("taskList") &&
    runListItemCommand(editor, "liftListItem", "taskItem")
  ) {
    return;
  }

  if (
    isListSelection(editor) &&
    runListItemCommand(editor, "liftListItem", "listItem")
  ) {
    return;
  }

  runIfAvailable(editor, "outdent");
};

const BlogRichTextToolbar = ({ editor, onImageUpload }) => {
  const imageUploadInputRef = useRef(null);
  const imageBase64InputRef = useRef(null);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [isImageEmbedding, setIsImageEmbedding] = useState(false);
  const [, setSelectionTick] = useState(0);

  useEffect(() => {
    if (!editor) return undefined;

    const refreshSelectionState = () => {
      setSelectionTick((tick) => tick + 1);
    };

    editor.on("selectionUpdate", refreshSelectionState);
    editor.on("transaction", refreshSelectionState);

    return () => {
      editor.off("selectionUpdate", refreshSelectionState);
      editor.off("transaction", refreshSelectionState);
    };
  }, [editor]);

  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href || "";
    const href = window.prompt("Enter URL", previousUrl);

    if (href === null) return;
    if (!href) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
  };

  const insertImage = (src, alt) => {
    const imageAttrs = {
      src,
      alt,
      align: "center",
      inline: false,
    };

    if (editor?.commands?.setImageInline) {
      editor.commands.focus();
      editor.commands.setImageInline(imageAttrs);
      return;
    }

    runIfAvailable(editor, "setImage", imageAttrs);
  };

  const isImageSelected = editor.isActive("image");
  const isColumnsActive = editor.isActive("columns");
  const activeColumnCount = Number(editor.getAttributes("columns").cols);
  const imageAttributes = editor.getAttributes("image");
  const selectedImageAlign = imageAttributes.align || "left";
  const selectedImageWidth = imageAttributes.width
    ? String(imageAttributes.width)
    : "auto";
  const selectedImageSize = imageSizeList.some(
    (size) => size.value === selectedImageWidth,
  )
    ? selectedImageWidth
    : "custom";

  const updateSelectedImage = (attributes) => {
    if (!isImageSelected) {
      toast.error("Please select an image first");
      return;
    }

    const chain = editor.chain().focus();
    if (typeof chain.updateImage === "function") {
      chain.updateImage(attributes).run();
      return;
    }

    chain.updateAttributes("image", attributes).run();
  };

  const setSelectedImageAlign = (align) => {
    if (!isImageSelected) {
      toast.error("Please select an image first");
      return;
    }

    const chain = editor.chain().focus();
    if (typeof chain.setAlignImage === "function") {
      chain.setAlignImage(align).run();
      return;
    }

    updateSelectedImage({ align });
  };

  const setSelectedImageSize = (width) => {
    updateSelectedImage({
      width: width === "auto" ? null : Number(width),
      height: null,
    });

    window.requestAnimationFrame(() => syncColumnImagePercentages(editor));
  };

  const deleteSelectedColumn = () => {
    const chain = editor.chain().focus();

    if (activeColumnCount <= 1) {
      chain.deleteNode("columns").run();
      return;
    }

    chain.deleteCol().run();
  };

  const openS3ImageUpload = () => {
    if (!onImageUpload) {
      toast.error("S3 image upload is not available");
      return;
    }

    imageUploadInputRef.current?.click();
  };

  const openBase64ImageEmbed = () => {
    imageBase64InputRef.current?.click();
  };

  const insertImageFromUrl = () => {
    const src = window.prompt("Enter image URL");
    if (!src) return;

    insertImage(src);
  };

  const handleS3ImageChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    setIsImageUploading(true);

    try {
      const src = await onImageUpload(file);
      if (!src) {
        throw new Error("Unable to upload image");
      }

      insertImage(src, file.name);
    } catch (error) {
      toast.error(error?.message || "Unable to upload image");
    } finally {
      setIsImageUploading(false);
    }
  };

  const handleBase64ImageChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    setIsImageEmbedding(true);

    try {
      const src = await readFileAsDataUrl(file);
      insertImage(src, file.name);
    } catch (error) {
      toast.error(error?.message || "Unable to embed image");
    } finally {
      setIsImageEmbedding(false);
    }
  };

  const setVideo = () => {
    const src = window.prompt("Enter video URL");
    if (!src) return;

    runIfAvailable(editor, "setVideo", { src });
  };

  const setBlock = (value) => {
    if (value === "paragraph") {
      editor.chain().focus().setParagraph().run();
      return;
    }

    editor
      .chain()
      .focus()
      .toggleHeading({ level: Number(value) })
      .run();
  };

  return (
    <div className="blog-tiptap-toolbar">
      <input
        ref={imageUploadInputRef}
        type="file"
        accept="image/*"
        className="blog-tiptap-file-input"
        onChange={handleS3ImageChange}
      />
      <input
        ref={imageBase64InputRef}
        type="file"
        accept="image/*"
        className="blog-tiptap-file-input"
        onChange={handleBase64ImageChange}
      />
      <ToolbarButton
        title="Undo"
        onClick={() => editor.chain().focus().undo().run()}
      >
        <FaUndo />
      </ToolbarButton>
      <ToolbarButton
        title="Redo"
        onClick={() => editor.chain().focus().redo().run()}
      >
        <FaRedo />
      </ToolbarButton>
      <ToolbarButton
        title="Clear formatting"
        onClick={() =>
          editor.chain().focus().unsetAllMarks().clearNodes().run()
        }
      >
        <FaEraser />
      </ToolbarButton>

      <span className="blog-tiptap-divider" />

      <ToolbarSelect
        title="Block style"
        value={getBlockValue(editor)}
        onChange={setBlock}
      >
        <option value="paragraph">Paragraph</option>
        <option value="1">Heading 1</option>
        <option value="2">Heading 2</option>
        <option value="3">Heading 3</option>
      </ToolbarSelect>

      <ToolbarSelect
        title="Font family"
        value={
          editor.getAttributes("textStyle").fontFamily || defaultFontFamily
        }
        onChange={(value) => {
          if (value === defaultFontFamily) {
            runIfAvailable(editor, "unsetFontFamily");
            return;
          }
          runIfAvailable(editor, "setFontFamily", value);
        }}
      >
        {fontFamilyList.map((fontFamily) => (
          <option key={fontFamily} value={fontFamily}>
            {fontFamily}
          </option>
        ))}
      </ToolbarSelect>

      <ToolbarSelect
        title="Font size"
        value={editor.getAttributes("textStyle").fontSize || "16px"}
        onChange={(value) => {
          if (value === "Default") {
            runIfAvailable(editor, "unsetFontSize");
            return;
          }
          runIfAvailable(editor, "setFontSize", value);
        }}
      >
        {fontSizeList.map((fontSize) => (
          <option key={fontSize} value={fontSize}>
            {fontSize}
          </option>
        ))}
      </ToolbarSelect>

      <span className="blog-tiptap-divider" />

      <ToolbarButton
        title="Bold"
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <FaBold />
      </ToolbarButton>
      <ToolbarButton
        title="Italic"
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <FaItalic />
      </ToolbarButton>
      <ToolbarButton
        title="Underline"
        active={editor.isActive("underline")}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <FaUnderline />
      </ToolbarButton>
      <ToolbarButton
        title="Strike"
        active={editor.isActive("strike")}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <FaStrikethrough />
      </ToolbarButton>
      {/* <ToolbarButton
        title="Inline code"
        active={editor.isActive("code")}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <FaCode />
      </ToolbarButton>
      <ToolbarButton
        title="HTML view"
        active={editor.storage?.codeView?.isActive}
        onClick={() => runIfAvailable(editor, "toggleCodeView")}
      >
        <FaFileCode />
      </ToolbarButton> */}

      <label className="blog-tiptap-color" title="Text color">
        <FaFont />
        <input
          type="color"
          value={editor.getAttributes("textStyle").color || "#262626"}
          onChange={(event) =>
            runIfAvailable(editor, "setColor", event.target.value)
          }
        />
      </label>
      <label className="blog-tiptap-color" title="Highlight">
        <FaHighlighter />
        <input
          type="color"
          value={editor.getAttributes("highlight").color || "#FADB14"}
          onChange={(event) =>
            editor
              .chain()
              .focus()
              .toggleHighlight({ color: event.target.value })
              .run()
          }
        />
      </label>

      <span className="blog-tiptap-divider" />

      <ToolbarButton
        title="Bullet list"
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <FaListUl />
      </ToolbarButton>
      <ToolbarButton
        title="Ordered list"
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <FaListOl />
      </ToolbarButton>
      {/* <ToolbarButton
        title="Task list"
        active={editor.isActive("taskList")}
        onClick={() => editor.chain().focus().toggleTaskList().run()}
      >
        <FaTasks />
      </ToolbarButton> */}
      <ToolbarButton title="Outdent" onClick={() => outdentSelection(editor)}>
        <FaOutdent />
      </ToolbarButton>
      <ToolbarButton title="Indent" onClick={() => indentSelection(editor)}>
        <FaIndent />
      </ToolbarButton>

      <ToolbarSelect
        title="Text alignment"
        value={
          ["left", "center", "right", "justify"].find((align) =>
            editor.isActive({ textAlign: align }),
          ) || "left"
        }
        onChange={(value) => editor.chain().focus().setTextAlign(value).run()}
      >
        <option value="left">Left</option>
        <option value="center">Center</option>
        <option value="right">Right</option>
        <option value="justify">Justify</option>
      </ToolbarSelect>

      <ToolbarSelect
        title="Line height"
        value={
          editor.getAttributes("paragraph").lineHeight ||
          editor.getAttributes("heading").lineHeight ||
          "1.5"
        }
        onChange={(value) => {
          if (value === "Default") {
            runIfAvailable(editor, "unsetLineHeight");
            return;
          }
          runIfAvailable(editor, "setLineHeight", value);
        }}
      >
        {lineHeightList.map((lineHeight) => (
          <option key={lineHeight} value={lineHeight}>
            {lineHeight}
          </option>
        ))}
      </ToolbarSelect>

      <span className="blog-tiptap-divider" />

      <ToolbarButton
        title="Link"
        active={editor.isActive("link")}
        onClick={setLink}
      >
        <FaLink />
      </ToolbarButton>
      <ToolbarButton
        title={isImageUploading ? "Uploading image..." : "Upload image to S3"}
        disabled={isImageUploading}
        onClick={openS3ImageUpload}
      >
        <FaCloudUploadAlt />
      </ToolbarButton>
      <ToolbarButton
        title={
          isImageEmbedding ? "Embedding image..." : "Embed image as base64"
        }
        disabled={isImageEmbedding}
        onClick={openBase64ImageEmbed}
      >
        <FaFileImage />
      </ToolbarButton>
      <ToolbarButton title="Insert image from URL" onClick={insertImageFromUrl}>
        <FaImage />
      </ToolbarButton>
      <ToolbarButton
        title="Align selected image left"
        active={isImageSelected && selectedImageAlign === "left"}
        disabled={!isImageSelected}
        onClick={() => setSelectedImageAlign("left")}
      >
        <FaAlignLeft />
      </ToolbarButton>
      <ToolbarButton
        title="Align selected image center"
        active={isImageSelected && selectedImageAlign === "center"}
        disabled={!isImageSelected}
        onClick={() => setSelectedImageAlign("center")}
      >
        <FaAlignCenter />
      </ToolbarButton>
      <ToolbarButton
        title="Align selected image right"
        active={isImageSelected && selectedImageAlign === "right"}
        disabled={!isImageSelected}
        onClick={() => setSelectedImageAlign("right")}
      >
        <FaAlignRight />
      </ToolbarButton>
      <ToolbarSelect
        title="Selected image size"
        disabled={!isImageSelected}
        value={selectedImageSize}
        onChange={setSelectedImageSize}
      >
        {imageSizeList.map((size) => (
          <option key={size.value} value={size.value}>
            {size.label}
          </option>
        ))}
        {selectedImageSize === "custom" && (
          <option value="custom" disabled>
            Custom
          </option>
        )}
      </ToolbarSelect>
      <ToolbarButton title="Video" onClick={setVideo}>
        <FaVideo />
      </ToolbarButton>
      <ToolbarButton
        title="Blockquote"
        active={editor.isActive("blockquote")}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <FaQuoteRight />
      </ToolbarButton>
      <ToolbarButton
        title="Horizontal rule"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        <FaMinus />
      </ToolbarButton>

      <span className="blog-tiptap-divider" />

      <ToolbarButton
        title="Insert table"
        onClick={() =>
          editor
            .chain()
            .focus()
            .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
            .run()
        }
      >
        <FaTable />
      </ToolbarButton>
      <ToolbarButton
        title="Add row"
        onClick={() => editor.chain().focus().addRowAfter().run()}
      >
        <FaPlusSquare />
      </ToolbarButton>
      <ToolbarButton
        title="Add column"
        onClick={() => editor.chain().focus().addColumnAfter().run()}
      >
        <FaColumns />
      </ToolbarButton>
      <ToolbarButton
        title="Delete table"
        onClick={() => editor.chain().focus().deleteTable().run()}
      >
        <FaTrashAlt />
      </ToolbarButton>

      <span className="blog-tiptap-divider" />

      <ToolbarButton
        title="Insert 2 columns"
        active={isColumnsActive && activeColumnCount === 2}
        onClick={() =>
          editor.chain().focus().insertColumns({ cols: 2 }).run()
        }
      >
        <FaColumns />
      </ToolbarButton>
      <ToolbarButton
        title="Insert 3 columns"
        active={isColumnsActive && activeColumnCount === 3}
        onClick={() =>
          editor.chain().focus().insertColumns({ cols: 3 }).run()
        }
      >
        <FaColumns />
      </ToolbarButton>
      <ToolbarButton
        title="Add column"
        active={isColumnsActive}
        disabled={!isColumnsActive}
        onClick={() => editor.chain().focus().addColAfter().run()}
      >
        <FaPlusSquare />
      </ToolbarButton>
      <ToolbarButton
        title={
          activeColumnCount <= 1
            ? "Delete columns block"
            : "Delete selected column"
        }
        disabled={!isColumnsActive}
        onClick={deleteSelectedColumn}
      >
        <FaTrashAlt />
      </ToolbarButton>
    </div>
  );
};

const BlogRichTextEditor = ({ value, onChange, onImageUpload }) => {
  const contentHtml = value?.contentHtml || "";
  const locallyEmittedHtmlRef = useRef(new Set());

  const extensions = useMemo(() => {
    const DocumentWithColumns = Document.extend({
      content: "(block|columns)+",
    });
    const ColumnAwareImage = Image.extend({
      addAttributes() {
        const parentAttributes = this.parent?.() || {};

        return {
          ...parentAttributes,
          width: {
            ...parentAttributes.width,
            default: null,
            parseHTML: (element) =>
              parseImagePixelDimension(element, "width"),
            renderHTML: (attributes) =>
              attributes.width ? { width: attributes.width } : {},
          },
          height: {
            ...parentAttributes.height,
            default: null,
            parseHTML: (element) =>
              parseImagePixelDimension(element, "height"),
            renderHTML: (attributes) =>
              attributes.height ? { height: attributes.height } : {},
          },
          columnWidthPercent: {
            default: null,
            parseHTML: (element) => {
              const value = element.getAttribute(
                "data-column-width-percent",
              );
              return value ? Number.parseFloat(value) : null;
            },
            renderHTML: (attributes) => {
              if (!attributes.columnWidthPercent) return {};

              return {
                "data-column-width-percent": attributes.columnWidthPercent,
                style: `--blog-column-image-width: ${attributes.columnWidthPercent}%`,
              };
            },
          },
        };
      },
    }).configure({
      resourceImage: onImageUpload ? "upload" : "link",
      ...(onImageUpload && { upload: onImageUpload }),
      defaultInline: false,
      enableAlt: true,
    });
    const MultipleColumnNodeWithHtmlCompatibility = MultipleColumnNode.extend({
      parseHTML() {
        return [
          { tag: "div.columns" },
          { tag: "div.grid" },
        ];
      },
    });

    return [
      DocumentWithColumns,
      Text,
      Dropcursor,
      Gapcursor,
      HardBreak,
      Paragraph,
      TrailingNode,
      ListItem,
      TextStyle,
      Placeholder.configure({
        placeholder: "Write blog content here",
      }),
      History,
      Clear,
      FontFamily.configure({
        fontFamilyList,
      }),
      FontSize.configure({
        types: ["textStyle"],
        fontSizes: fontSizeList,
      }),
      Heading,
      Bold,
      Italic,
      TextUnderline,
      Strike,
      MoreMark,
      Color.configure({
        colors: colorList,
        defaultColor: "#262626",
      }),
      Highlight.configure({
        multicolor: true,
        defaultColor: "#FADB14",
      }),
      BulletList,
      OrderedList,
      TaskList,
      TextAlign.configure({
        types: ["heading", "paragraph"],
        alignments: ["left", "center", "right", "justify"],
      }),
      Indent.configure({
        types: ["heading", "paragraph"],
        minIndent: 0,
        maxIndent: 8,
      }),
      TextDirection,
      BlockLineHeight,
      Link.configure({
        autolink: true,
        openOnClick: false,
        linkOnPaste: true,
      }),
      ColumnAwareImage,
      Video.configure({
        resourceVideo: "link",
        videoProviders: ["."],
      }),
      Blockquote,
      HorizontalRule,
      Code,
      CodeView,
      Column,
      ColumnNode,
      MultipleColumnNodeWithHtmlCompatibility,
      Table.configure({
        resizable: true,
      }),
      Callout,
      SearchAndReplace,
    ];
  }, [onImageUpload]);

  const editor = useEditor({
    content: contentHtml,
    extensions,
    textDirection: "auto",
    editorProps: {
      attributes: {
        class: "blog-tiptap-prosemirror",
      },
    },
    onUpdate: ({ editor: currentEditor }) => {
      const nextContentHtml = currentEditor.getHTML();
      const locallyEmittedHtml = locallyEmittedHtmlRef.current;

      locallyEmittedHtml.add(nextContentHtml);
      if (locallyEmittedHtml.size > 50) {
        locallyEmittedHtml.delete(locallyEmittedHtml.values().next().value);
      }

      onChange({
        contentHtml: nextContentHtml,
        contentJson: JSON.stringify(currentEditor.getJSON()),
      });
    },
  });

  useEffect(() => {
    if (!editor) return;

    if (locallyEmittedHtmlRef.current.has(contentHtml)) {
      return;
    }

    const currentHtml = editor.getHTML();
    if (contentHtml !== currentHtml) {
      locallyEmittedHtmlRef.current.clear();
      editor.commands.setContent(contentHtml, { emitUpdate: false });
    }
  }, [contentHtml, editor]);

  useEffect(() => {
    if (!editor) return undefined;

    let animationFrame;
    const handleResizeEnd = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        syncColumnImagePercentages(editor);
      });
    };

    document.addEventListener("mouseup", handleResizeEnd);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      document.removeEventListener("mouseup", handleResizeEnd);
    };
  }, [editor]);

  return (
    <div className="blog-tiptap-editor">
      <BlogRichTextToolbar editor={editor} onImageUpload={onImageUpload} />
      <EditorContent editor={editor} />
    </div>
  );
};

export default BlogRichTextEditor;
